'use client';
import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import Link from 'next/link';
import {
  getWorldCupStandings,
  getFlag,
  type ApiStanding,
} from '@/lib/api-football';
import {
  Users,
  Trophy,
  TrendingUp,
  Target,
  RefreshCw,
  Search,
  Shield,
  Swords,
  ChevronRight,
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface GroupData {
  name: string;
  rows: ApiStanding[];
}

const FORM_COLORS: Record<string, { bg: string; text: string }> = {
  W: { bg: 'hsl(142 71% 45% / 0.2)', text: 'hsl(142 71% 55%)' },
  D: { bg: 'hsl(222 25% 22%)', text: 'hsl(222 15% 60%)' },
  L: { bg: 'hsl(0 84% 60% / 0.2)', text: 'hsl(0 84% 65%)' },
};

function deriveForm(wins: number, draws: number, losses: number): string[] {
  const results: string[] = [];
  let w = wins, d = draws, l = losses;
  const total = Math.min(w + d + l, 5);
  for (let i = 0; i < total; i++) {
    if (w > 0 && (i % 2 === 0 || d === 0)) { results.push('W'); w--; }
    else if (l > 0 && i % 3 === 2) { results.push('L'); l--; }
    else if (d > 0) { results.push('D'); d--; }
    else if (w > 0) { results.push('W'); w--; }
    else if (l > 0) { results.push('L'); l--; }
  }
  return results;
}

function TeamCard({ standing, rank }: { standing: ApiStanding; rank: number }) {
  const { team, all, points, goalsDiff, group } = standing;
  const { t } = useLanguage();
  const winRate = all.played > 0 ? Math.round((all.win / all.played) * 100) : 0;
  const form = deriveForm(all.win, all.draw, all.lose);
  const isQualified = standing.rank <= 2;
  const flag = getFlag(team.name);

  return (
    <Link
      href={`/team/${team.id}`}
      className="group block rounded-2xl overflow-hidden transition-all duration-200 hover:scale-[1.02] hover:shadow-xl"
      style={{
        background: 'hsl(222 35% 11%)',
        border: '1px solid hsl(222 25% 16%)',
        boxShadow: '0 2px 12px hsl(222 40% 5% / 0.4)',
      }}
    >
      {/* Card Header */}
      <div
        className="relative px-4 pt-4 pb-3 overflow-hidden"
        style={{ background: 'hsl(222 35% 13%)' }}
      >
        {/* Subtle glow */}
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 20% 50%, hsl(25 95% 53% / 0.08), transparent 70%)' }}
        />

        <div className="flex items-center gap-3 relative">
          {/* Team Logo / Flag */}
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-hover:scale-105"
            style={{ background: 'hsl(222 35% 17%)' }}
          >
            {team.logo ? (
              <img src={team.logo} alt={team.name} className="w-10 h-10 object-contain" />
            ) : (
              <span className="text-3xl">{flag}</span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-base font-black text-white tracking-tight truncate group-hover:text-orange-300 transition-colors">
              {team.name}
            </h3>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span
                className="text-xs px-2 py-0.5 rounded-full font-semibold"
                style={{ background: 'hsl(25 95% 53% / 0.12)', color: 'hsl(25 95% 65%)' }}
              >
                {group}
              </span>
              {isQualified && (
                <span
                  className="text-xs px-2 py-0.5 rounded-full font-semibold"
                  style={{ background: 'hsl(189 94% 51% / 0.12)', color: 'hsl(189 94% 60%)' }}
                >
                  {t.teams.qualified}
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <span className="text-2xl font-black text-white">{points}</span>
            <span className="text-xs text-slate-500">{t.standings.points.toLowerCase()}</span>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-px" style={{ background: 'hsl(222 25% 14%)' }}>
        {[
          { label: t.teams.played, value: all.played, color: 'text-slate-300' },
          { label: t.teams.won, value: all.win, color: 'text-green-400' },
          { label: t.standings.goalsFor, value: all.goals.for, color: 'text-cyan-400' },
          { label: t.standings.goalDiff, value: goalsDiff > 0 ? `+${goalsDiff}` : goalsDiff, color: goalsDiff > 0 ? 'text-green-400' : goalsDiff < 0 ? 'text-red-400' : 'text-slate-400' },
        ].map(s => (
          <div
            key={s.label}
            className="flex flex-col items-center py-2.5"
            style={{ background: 'hsl(222 35% 11%)' }}
          >
            <span className={`text-base font-black ${s.color}`}>{s.value}</span>
            <span className="text-xs text-slate-600 mt-0.5">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Form + Win Rate */}
      <div className="px-4 py-3 flex items-center justify-between gap-3">
        {/* Recent Form */}
        <div className="flex items-center gap-1.5">
          {form.length > 0 ? form.map((r, i) => {
            const style = FORM_COLORS[r] ?? FORM_COLORS.D;
            return (
              <span
                key={i}
                className="w-6 h-6 rounded-md flex items-center justify-center text-xs font-black"
                style={{ background: style.bg, color: style.text }}
              >
                {r}
              </span>
            );
          }) : (
            <span className="text-xs text-slate-600">{t.common.noData}</span>
          )}
        </div>

        {/* Win Rate */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="text-right">
            <div className="text-xs text-slate-500">{t.teams.winRate}</div>
            <div className="text-sm font-bold text-white">{winRate}%</div>
          </div>
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{
              background: `conic-gradient(hsl(25 95% 53%) ${winRate * 3.6}deg, hsl(222 25% 18%) 0deg)`,
            }}
          >
            <div
              className="w-5 h-5 rounded-full"
              style={{ background: 'hsl(222 35% 11%)' }}
            />
          </div>
        </div>
      </div>

      {/* View Profile CTA */}
      <div
        className="px-4 py-2.5 flex items-center justify-between border-t"
        style={{ borderColor: 'hsl(222 25% 14%)', background: 'hsl(222 35% 10%)' }}
      >
        <span className="text-xs text-slate-500">{t.team.roster} · {t.team.fixtures} · {t.team.history}</span>
        <ChevronRight size={14} className="text-slate-600 group-hover:text-orange-400 transition-colors" />
      </div>
    </Link>
  );
}

export default function TeamsPage() {
  const [groups, setGroups] = useState<GroupData[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeGroup, setActiveGroup] = useState<string>('all');
  const [lastUpdate, setLastUpdate] = useState('');
  const { t, language } = useLanguage();

  const fetchData = useCallback(async () => {
    setLoading(true);
    const data = await getWorldCupStandings();
    if (data && data.length > 0) {
      const parsed: GroupData[] = data.map((groupRows, idx) => ({
        name: groupRows[0]?.group ?? `Group ${String.fromCharCode(65 + idx)}`,
        rows: groupRows,
      }));
      setGroups(parsed);
    }
    setLoading(false);
    setLastUpdate(new Date().toLocaleTimeString(language === 'es' ? 'es-ES' : 'en-US'));
  }, [language]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const allTeams = groups.flatMap(g => g.rows);
  const groupNames = groups.map(g => g.name);

  const filteredTeams = allTeams.filter(team => {
    const matchesSearch = team.team.name.toLowerCase().includes(search.toLowerCase());
    const matchesGroup = activeGroup === 'all' || team.group === activeGroup;
    return matchesSearch && matchesGroup;
  });

  const totalGoals = allTeams.reduce((sum, team) => sum + team.all.goals.for, 0);
  const topScorer = [...allTeams].sort((a, b) => b.all.goals.for - a.all.goals.for)[0];

  return (
    <AppLayout activeRoute="/teams">
      <div className="space-y-6 pb-20 lg:pb-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
              <Shield size={22} className="text-orange-400" />
              {t.teams.title}
            </h1>
            <p className="text-sm text-slate-400 mt-0.5">
              {t.teams.subtitle}
            </p>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin text-cyan-500' : ''} />
            {lastUpdate && <span className="font-mono">{lastUpdate}</span>}
          </button>
        </div>

        {/* Summary Stats */}
        {!loading && allTeams.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { icon: <Users size={16} className="text-orange-400" />, label: t.teams.title, value: allTeams.length },
              { icon: <Trophy size={16} className="text-yellow-400" />, label: t.standings.title, value: groups.length },
              { icon: <Target size={16} className="text-cyan-400" />, label: t.team.goals, value: totalGoals },
              {
                icon: <TrendingUp size={16} className="text-green-400" />,
                label: t.standings.groupLeaders,
                value: topScorer ? topScorer.team.name.split(' ')[0] : '—',
              },
            ].map(s => (
              <div
                key={s.label}
                className="rounded-xl p-3 flex items-center gap-3"
                style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: 'hsl(222 35% 16%)' }}
                >
                  {s.icon}
                </div>
                <div className="min-w-0">
                  <div className="text-base font-black text-white truncate">{s.value}</div>
                  <div className="text-xs text-slate-500 truncate">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Search + Group Filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              placeholder={t.teams.searchPlaceholder}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm text-white placeholder-slate-600 outline-none transition-all"
              style={{
                background: 'hsl(222 35% 13%)',
                border: '1px solid hsl(222 25% 18%)',
              }}
            />
          </div>

          {/* Group Filter */}
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-thin pb-0.5">
            {(['all', ...groupNames] as const).map(g => (
              <button
                key={g}
                onClick={() => setActiveGroup(g)}
                className="flex-shrink-0 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-150"
                style={{
                  background: activeGroup === g ? 'hsl(25 95% 53%)' : 'hsl(222 35% 13%)',
                  color: activeGroup === g ? 'white' : 'hsl(222 15% 55%)',
                  border: activeGroup === g ? 'none' : '1px solid hsl(222 25% 18%)',
                }}
              >
                {g === 'all' ? t.teams.allGroups : g}
              </button>
            ))}
          </div>
        </div>

        {/* Team Cards Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="rounded-2xl animate-pulse"
                style={{ background: 'hsl(222 35% 11%)', height: 220 }}
              />
            ))}
          </div>
        ) : filteredTeams.length === 0 ? (
          <div
            className="rounded-2xl p-12 text-center"
            style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
          >
            <div className="text-4xl mb-3">🔍</div>
            <p className="text-slate-300 font-semibold">{t.teams.noTeams}</p>
            <p className="text-slate-500 text-sm mt-1">{t.common.retry}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredTeams.map(standing => (
              <TeamCard key={standing.team.id} standing={standing} rank={standing.rank} />
            ))}
          </div>
        )}

        {/* Group Leaders Section */}
        {!loading && groups.length > 0 && activeGroup === 'all' && !search && (
          <div
            className="rounded-2xl p-4"
            style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
          >
            <div className="flex items-center gap-2 mb-4">
              <Swords size={15} className="text-orange-400" />
              <span className="text-sm font-semibold text-white">{t.standings.groupLeaders}</span>
              <span className="ml-auto text-xs text-slate-500">{t.teams.viewTeam}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {groups.map(group => {
                const leader = group.rows[0];
                if (!leader) return null;
                return (
                  <Link
                    key={group.name}
                    href={`/team/${leader.team.id}`}
                    className="rounded-xl p-3 text-center hover:opacity-80 transition-all hover:scale-105 block"
                    style={{ background: 'hsl(222 35% 14%)' }}
                  >
                    {leader.team.logo ? (
                      <img
                        src={leader.team.logo}
                        alt={leader.team.name}
                        className="w-9 h-9 object-contain mx-auto mb-1.5"
                      />
                    ) : (
                      <div className="text-2xl mb-1.5">{getFlag(leader.team.name)}</div>
                    )}
                    <div className="text-xs font-bold text-white truncate">{leader.team.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{group.name}</div>
                    <div
                      className="text-xs font-semibold mt-1 px-1.5 py-0.5 rounded-full inline-block"
                      style={{ background: 'hsl(25 95% 53% / 0.15)', color: 'hsl(25 95% 65%)' }}
                    >
                      {leader.points} {t.standings.points.toLowerCase()}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
